
import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { primeEffects, getSoundManager } from "@/utils/effects";
import { GlowButton } from "@/components/ui/glow-button";

export const CreateGameButton = () => {
  const buttonRef = useRef<HTMLAnchorElement>(null);
  
  // Prime effects when component mounts to ensure sounds are ready for this important button
  useEffect(() => {
    // Prime effects to ensure sounds are loaded
    primeEffects();
    
    // Try to unlock audio right away
    const soundManager = getSoundManager();
    if (soundManager) {
      soundManager.tryUnlockAudio();
      
      // Preload button down sound with highest priority
      soundManager.preloadSound('buttonDown', true).catch(e => {
        console.warn("Failed to preload buttonDown sound:", e);
      });
    }
    
    // Add direct touch event listeners for mobile
    if (buttonRef.current) {
      const primeTouchEffects = () => {
        // Re-prime on user interaction to ensure mobile audio works
        primeEffects();
        
        // Try to unlock audio explicitly
        if (soundManager) {
          soundManager.tryUnlockAudio();
        }
      };
      
      // Listen for both touch and pointer events
      buttonRef.current.addEventListener('touchstart', primeTouchEffects, { once: true, passive: true });
      buttonRef.current.addEventListener('pointerdown', primeTouchEffects, { once: true, passive: true });
      
      return () => {
        if (buttonRef.current) {
          buttonRef.current.removeEventListener('touchstart', primeTouchEffects);
          buttonRef.current.removeEventListener('pointerdown', primeTouchEffects);
        }
      };
    }
  }, []);
  
  return (
    <motion.div 
      className="flex justify-center mb-8"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.4 }}
    >
      <Link to="/create" ref={buttonRef}>
        <GlowButton 
          size="lg" 
          className="gap-1 px-5 py-2.5"
          glowColors={['#33C3F0', '#0EA5E9', '#8B5CF6', '#D946EF']}
          glowMode="flowHorizontal"
          glowBlur="strong"
          glowScale={1.5}
        >
          <span>Create New Match</span>
          <ChevronRight className="h-4 w-4" />
        </GlowButton>
      </Link>
    </motion.div>
  );
};
